import type { Filter, Amenity } from '@cvent/venue-search-shared/types';
import type { AmenityCategory } from '@/utils/filters/options/getAmenityOptions';
import { useCallback, useState } from 'react';
import Icon from '@/components/shared/Icons';
import dynamic from 'next/dynamic';
import type { CheckboxGroupOption } from '@cvent/carina/components/Checkbox/CheckboxGroup/CheckboxGroup';
import { convertToSnakeCase } from '@/utils/analyticsClientHelper';

const CheckboxGroup = dynamic(() => import('@/components/shared/checkbox/CheckboxGroup'), { ssr: false });
const TextLink = dynamic(() => import('@/components/shared/TextLink'), { ssr: false });

type Props = {
  selected: Filter;
  options: Array<AmenityCategory>;
  setSelected: (selections: Filter) => void;
  labels: {
    title: string;
    selectAll: string;
    clear: string;
  };
  testID?: string;
};

export default function AmenitiesFilter({ options, selected, setSelected, labels, testID }: Props) {
  const [category, setCategory] = useState<string>('');
  const onUpdate = useCallback(
    selections => {
      const isOutdoorSpaceSelected = selections.includes('OUTDOOR_SPACE');
      const venueDetails = selected?.VENUE_DETAILS || [];
      let updatedVenueDetails = venueDetails;
      if (!isOutdoorSpaceSelected && venueDetails.includes('OUTDOOR_SPACE')) {
        updatedVenueDetails = venueDetails.filter(item => item !== 'OUTDOOR_SPACE');
      }
      if (isOutdoorSpaceSelected && !venueDetails.includes('OUTDOOR_SPACE')) {
        updatedVenueDetails = venueDetails?.concat('OUTDOOR_SPACE');
      }
      setSelected({
        ...selected,
        AMENITIES: selections,
        VENUE_DETAILS: updatedVenueDetails
      });
    },
    [selected, setSelected]
  );

  const updateCategory = useCallback(
    (categoryKey: string) => {
      if (categoryKey === category) {
        setCategory('');
      } else {
        setCategory(categoryKey);
      }
    },
    [category]
  );

  const getOptions = useCallback(
    (amenityOptions: { label: string; value: string }[]): CheckboxGroupOption[] =>
      amenityOptions.map(option => ({
        label: <div className="-mt-0.5 text-b-md/snug">{option.label}</div>,
        value: option.value
      })),
    []
  );

  const getOptionValues = useCallback(
    (amenityOptions: { value: Amenity }[]): Amenity[] => amenityOptions.map(option => option.value),
    []
  );

  const onSelectAll = useCallback(
    (values: Amenity[], categoryKey: string) => {
      const result = values.concat(selected.AMENITIES?.filter(value => !values.includes(value)) || []);
      setSelected({ ...selected, AMENITIES: result });
      setCategory(categoryKey);
    },
    [selected, setSelected]
  );

  const onClearAll = useCallback(
    (values: Amenity[], categoryKey: string) => {
      setSelected({
        ...selected,
        AMENITIES: selected.AMENITIES?.filter(value => !values.includes(value))
      });
      setCategory(categoryKey);
    },
    [selected, setSelected]
  );

  const selectAllButton = useCallback(
    (values: Amenity[], categoryKey: string, id: string) => {
      const disabled = values.every(r => selected.AMENITIES?.includes(r));
      return (
        <div className="mr-4 text-b-xs/tight">
          <TextLink
            ariaLabel={labels.selectAll}
            testID={`${testID}-${convertToSnakeCase(id)}-select-all-button`}
            className={`font-normal ${disabled ? 'text-neutral-40' : 'text-brand-60'}`}
            disabled={disabled}
            onClick={
              disabled
                ? undefined
                : e => {
                    e.stopPropagation();
                    onSelectAll(values, categoryKey);
                  }
            }
            onKeyUp={e => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                onSelectAll(values, categoryKey);
              }
            }}
          >
            {labels.selectAll}
          </TextLink>
        </div>
      );
    },
    [labels.selectAll, onSelectAll, selected.AMENITIES, testID]
  );

  const clearButton = useCallback(
    (values: Amenity[], categoryKey: string, id: string) => {
      const disabled = !selected.AMENITIES?.some(r => values.includes(r));
      return (
        <div className="mr-4 text-b-xs/tight">
          <TextLink
            ariaLabel={labels.clear}
            testID={`${testID}-${convertToSnakeCase(id)}-clear-button`}
            className={`font-normal ${disabled ? 'text-neutral-40' : 'text-brand-60'}`}
            disabled={disabled}
            onClick={
              disabled
                ? undefined
                : e => {
                    e.stopPropagation();
                    onClearAll(values, categoryKey);
                  }
            }
            onKeyUp={e => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                onClearAll(values, categoryKey);
              }
            }}
          >
            {labels.clear}
          </TextLink>
        </div>
      );
    },
    [labels.clear, onClearAll, selected.AMENITIES, testID]
  );

  const child: JSX.Element = (
    <>
      {options.map(option => {
        if (option.categoryName) {
          return (
            <div className="mb-1" key={option.categoryName}>
              <div
                role="button"
                tabIndex={0}
                className="flex h-11 items-center justify-between"
                onClick={() => updateCategory(option.key)}
                aria-label={option.categoryName}
                onKeyUp={event => {
                  if (event.key === 'Enter') {
                    updateCategory(option.key);
                  }
                }}
              >
                <div className="flex items-center">
                  <Icon id={option.icon} className="mr-2 h-6 w-6" />
                  <div className="text-b-md/snug">{option.categoryName}</div>
                </div>
                <div className="flex items-center">
                  <div
                    className="invisible lg:visible"
                    data-cvent-id={`${testID}-${convertToSnakeCase(option.categoryName)}-select-button`}
                  >
                    {selectAllButton(getOptionValues(option.values), option.key, option.categoryName)}
                  </div>
                  <div
                    className="invisible lg:visible"
                    data-cvent-id={`${testID}-${convertToSnakeCase(option.categoryName)}-clear-button`}
                  >
                    {clearButton(getOptionValues(option.values), option.key, option.categoryName)}
                  </div>
                  <Icon
                    id={category === option.key ? 'chevron-up' : 'chevron-down'}
                    testID={
                      category === option.key
                        ? `${testID}-${convertToSnakeCase(option.categoryName)}-chevron-up`
                        : `${testID}-${convertToSnakeCase(option.categoryName)}-chevron-down`
                    }
                    className="ml-2 h-4 w-4"
                  />
                </div>
              </div>
              <div className="h-6 lg:hidden">
                <div className="flex items-center justify-end">
                  {selectAllButton(getOptionValues(option.values), option.key, option.categoryName)}
                  {clearButton(getOptionValues(option.values), option.key, option.categoryName)}
                </div>
              </div>
              {category === option.key && (
                <div className="pl-6">
                  <CheckboxGroup
                    name={`${option.categoryName}-options`}
                    labelText=""
                    testID={`${testID}-${convertToSnakeCase(option.categoryName)}-options`}
                    options={getOptions(option.values)}
                    selected={selected.AMENITIES}
                    onUpdate={onUpdate}
                  />
                </div>
              )}
            </div>
          );
        }
        return (
          <div key="default-amenity-types">
            <CheckboxGroup
              name="amenity-type-options"
              labelText=""
              testID={`${testID}-amenity-type-options`}
              options={getOptions(option.values)}
              selected={selected.AMENITIES}
              onUpdate={onUpdate}
            />
          </div>
        );
      })}
    </>
  );

  return (
    <div className="border-y border-neutral-20 py-3">
      <div className="mb-2 text-b-xl/loose font-medium">{labels.title}</div>
      {child}
    </div>
  );
}
